import axios from 'axios';
import { getToken, clearAuth } from './authToken';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '/api',
  timeout: 30000,
});

api.interceptors.request.use((config) => {
  const token = getToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401) {
      clearAuth();
      window.location.href = '/login';
    }
    return Promise.reject(err);
  }
);

// ── 시스템 콘솔 MFA 인증 전용 클라이언트 ──
// 전역 api의 401→/login 리다이렉트 인터셉터를 타지 않도록 별도 인스턴스 사용
// (TOTP 코드 오류 시 로그인 화면으로 튕기지 않고 재입력 유도)
const authClient = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '/api',
  timeout: 30000,
});

export const consoleAuthAPI = {
  // 1단계: ID/PW → mfa_pending 토큰
  login: (username: string, password: string) =>
    authClient.post('/console/auth/login', { username, password }),
  // 최초 등록: QR/otpauth/secret
  mfaSetup: (mfaToken: string) =>
    authClient.post('/console/auth/mfa/setup', {}, { headers: { Authorization: `Bearer ${mfaToken}` } }),
  // 최초 등록 확정: 첫 코드 검증 → 백업코드
  mfaEnable: (mfaToken: string, code: string) =>
    authClient.post('/console/auth/mfa/enable', { code }, { headers: { Authorization: `Bearer ${mfaToken}` } }),
  // 2단계: TOTP/백업코드 → 콘솔 접근 토큰
  mfaVerify: (mfaToken: string, code: string) =>
    authClient.post('/console/auth/mfa/verify', { code }, { headers: { Authorization: `Bearer ${mfaToken}` } }),
};

export const protocolAPI = {
  list: () => api.get('/protocols'),
  get: (id: number) => api.get(`/protocols/${id}`),
  create: (data: any) => api.post('/protocols', data),
  update: (id: number, data: any) => api.put(`/protocols/${id}`, data),
  delete: (id: number) => api.delete(`/protocols/${id}`),
  downloadHwpx: (id: number) =>
    api.get(`/protocols/${id}/hwpx`, { responseType: 'blob' }),
  uploadHwpx: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/protocols/upload-hwpx', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  previewHwpx: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/protocols/preview-hwpx', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

export const scheduleAPI = {
  list: (params?: { search?: string; test_year?: string; category_id?: number; company_id?: number; location_id?: number; status?: string }) =>
    api.get('/schedules', { params }),
  get: (id: number) => api.get(`/schedules/${id}`),
  create: (data: any) => api.post('/schedules', data),
  update: (id: number, data: any) => api.put(`/schedules/${id}`, data),
  delete: (id: number) => api.delete(`/schedules/${id}`),
  bulk: (data: any) => api.post('/schedules/bulk', data),
  stats: () => api.get('/schedules/stats'),
  filters: () => api.get('/schedules/filters'),
  uploadExcel: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/schedules/upload-excel', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  exportExcel: (test_year?: string) =>
    api.get('/schedules/export/excel', { params: { test_year }, responseType: 'blob' }),
  downloadTemplate: () =>
    api.get('/schedules/template', { responseType: 'blob' }),
};

// 마스터 API
export const masterAPI = {
  // 의뢰사
  companies: {
    list: () => api.get('/master/companies'),
    create: (data: any) => api.post('/master/companies', data),
    update: (id: number, data: any) => api.put(`/master/companies/${id}`, data),
    delete: (id: number) => api.delete(`/master/companies/${id}`),
  },
  // 시험장소
  locations: {
    list: () => api.get('/master/locations'),
    create: (data: any) => api.post('/master/locations', data),
    update: (id: number, data: any) => api.put(`/master/locations/${id}`, data),
    delete: (id: number) => api.delete(`/master/locations/${id}`),
  },
  // 구분
  categories: {
    list: () => api.get('/master/categories'),
    create: (data: any) => api.post('/master/categories', data),
    update: (id: number, data: any) => api.put(`/master/categories/${id}`, data),
    delete: (id: number) => api.delete(`/master/categories/${id}`),
  },
  // 포장주
  fieldOwners: {
    list: () => api.get('/master/field-owners'),
    create: (data: any) => api.post('/master/field-owners', data),
    update: (id: number, data: any) => api.put(`/master/field-owners/${id}`, data),
    delete: (id: number) => api.delete(`/master/field-owners/${id}`),
  },
  // 차량
  vehicles: {
    list: () => api.get('/master/vehicles'),
    create: (data: any) => api.post('/master/vehicles', data),
    update: (id: number, data: any) => api.put(`/master/vehicles/${id}`, data),
    delete: (id: number) => api.delete(`/master/vehicles/${id}`),
  },
  // 연구원
  researchers: {
    list: () => api.get('/master/researchers'),
    create: (data: any) => api.post('/master/researchers', data),
    update: (id: number, data: any) => api.put(`/master/researchers/${id}`, data),
    delete: (id: number) => api.delete(`/master/researchers/${id}`),
  },
  statusCodes: {
    list: () => api.get('/master/status-codes'),
    create: (data: any) => api.post('/master/status-codes', data),
    update: (id: number, data: any) => api.put(`/master/status-codes/${id}`, data),
    delete: (id: number) => api.delete(`/master/status-codes/${id}`),
  },
};

export const dashboardAPI = {
  stats: () => api.get('/dashboard'),
  alerts: () => api.get('/dashboard/alerts'),
  upcoming: (year: number, month: number) => api.get('/dashboard/upcoming', { params: { year, month } }),
};

// 권한 관리 API
export const authMeAPI = {
  me: () => api.get('/auth/me'),
  changePassword: (data: { current_password: string; new_password: string }) =>
    api.post('/auth/change-password', data),
};

export const permissionAPI = {
  list: () => api.get('/permissions'),
  matrix: () => api.get('/permissions/matrix'),
};

export const roleAPI = {
  list: () => api.get('/roles'),
  create: (data: { role_code: string; role_name: string; description?: string }) =>
    api.post('/roles', data),
  update: (id: number, data: { role_name?: string; description?: string; is_active?: boolean }) =>
    api.put(`/roles/${id}`, data),
  delete: (id: number) => api.delete(`/roles/${id}`),
  setPermissions: (id: number, data: { permission_action_ids: number[] }) =>
    api.put(`/roles/${id}/permissions`, data),
};

export const menuOrderAPI = {
  get: () => api.get('/permissions/menu-order'),
  save: (data: { groups: { group_name: string; group_sort_order: number; menus: { menu_code: string; sort_order: number; menu_name?: string }[] }[] }) =>
    api.put('/permissions/menu-order', data),
  reset: () => api.post('/permissions/menu-order/reset'),
};

export const userAPI = {
  list: () => api.get('/users'),
  create: (data: { username: string; password: string; full_name: string; role_ids: number[] }) =>
    api.post('/users', data),
  update: (id: number, data: { full_name?: string; password?: string; is_active?: boolean; role_ids?: number[] }) =>
    api.put(`/users/${id}`, data),
  delete: (id: number) => api.delete(`/users/${id}`),
  resetPassword: (id: number, data: { new_password: string }) =>
    api.post(`/users/${id}/reset-password`, data),
};

// 파트너 계정 관리 API
export const partnerAccountAPI = {
  list: () => api.get('/partner-accounts'),
  create: (data: { username: string; password: string; full_name: string; company_id: number }) =>
    api.post('/partner-accounts', data),
  update: (id: number, data: { full_name?: string; password?: string; company_id?: number }) =>
    api.put(`/partner-accounts/${id}`, data),
  delete: (id: number) => api.delete(`/partner-accounts/${id}`),
  scheduleAccess: {
    list: (accountId: number) => api.get(`/partner-accounts/${accountId}/schedule-access`),
    add: (accountId: number, scheduleIds: number[], note?: string) =>
      api.post(`/partner-accounts/${accountId}/schedule-access`, { schedule_ids: scheduleIds, note }),
    remove: (accountId: number, scheduleId: number) =>
      api.delete(`/partner-accounts/${accountId}/schedule-access/${scheduleId}`),
  },
  visibleSchedules: (accountId: number) => api.get(`/partner-accounts/${accountId}/visible-schedules`),
};

// 활동이력 API
export const activityLogAPI = {
  list: (params?: {
    action?: string; target_type?: string; username?: string; keyword?: string;
    date_from?: string; date_to?: string; page?: number; page_size?: number;
  }) => api.get('/activity-logs', { params }),
  stats: (params?: {
    action?: string; target_type?: string; username?: string; keyword?: string;
    date_from?: string; date_to?: string;
  }) => api.get('/activity-logs/stats', { params }),
  logPageView: (path: string, name: string) =>
    api.post('/activity-logs/page-view', { path, name }).catch(() => {}),
};

// 서버 모니터링 API
export const monitoringAPI = {
  serverStatus: () => api.get('/monitoring/server-status'),
  ociCost: () => api.get('/monitoring/oci-cost'),
  ghcr: () => api.get('/monitoring/ghcr'),
  backupStatus: () => api.get('/monitoring/backup-status'),
  containerLogs: (params: { container?: string; lines?: number; search?: string; level?: string }) =>
    api.get('/monitoring/container-logs', { params }),
  systemLogs: (params: { file: string; lines?: number }) =>
    api.get('/monitoring/system-logs', { params }),
};

// ADB 데모 (OCI Autonomous Database CRUD 예제)
export interface DemoItem {
  id: number;
  name: string;
  qty: number;
  note?: string;
  created_at?: string;
}
export const adbDemoAPI = {
  health: () => api.get('/adb-demo/health'),
  list: () => api.get('/adb-demo/items'),
  create: (data: { name: string; qty: number; note?: string }) =>
    api.post('/adb-demo/items', data),
  update: (id: number, data: { name: string; qty: number; note?: string }) =>
    api.put(`/adb-demo/items/${id}`, data),
  remove: (id: number) => api.delete(`/adb-demo/items/${id}`),
};

// 챗봇 API
export const chatbotAPI = {
  send: (message: string) => api.post('/chatbot', { message }),
  /** SSE 스트리밍 — onChunk: 토큰 단위, onReply: 전체 치환, onDone: 완료 */
  stream: async (
    message: string,
    handlers: { onChunk?: (t: string) => void; onReply?: (t: string) => void; onDone?: () => void; onError?: (e: unknown) => void }
  ): Promise<void> => {
    const base = process.env.REACT_APP_API_URL || '/api';
    const token = getToken() || '';
    try {
      const res = await fetch(`${base}/chatbot/stream`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ message }),
      });
      if (!res.ok || !res.body) throw new Error(`HTTP ${res.status}`);
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const events = buffer.split(String.fromCharCode(10)+String.fromCharCode(10));
        buffer = events.pop() || '';
        for (const ev of events) {
          const lines = ev.split(String.fromCharCode(10));
          const evName = lines.find((l) => l.startsWith('event: '))?.slice(7) || '';
          const dataLine = lines.find((l) => l.startsWith('data: '))?.slice(6) || '';
          if (!dataLine) continue;
          try {
            const data = JSON.parse(dataLine);
            if (evName === 'chunk' && data.text) handlers.onChunk?.(data.text);
            else if (evName === 'reply' && data.text) handlers.onReply?.(data.text);
            else if (evName === 'done') handlers.onDone?.();
          } catch {}
        }
      }
      handlers.onDone?.();
    } catch (e) {
      handlers.onError?.(e);
    }
  },
};

// 글로벌 검색 API (OpenSearch)
export const searchAPI = {
  global: (q: string) => api.get('/search', { params: { q } }),
};

// OpenSearch 관리 API
export const opensearchAPI = {
  health: () => api.get('/opensearch/health'),
  popularSearches: (days?: number) => api.get('/opensearch/popular-searches', { params: { days } }),
  resync: () => api.post('/opensearch/resync'),
};

// 출장 관리 API
export const businessTripAPI = {
  list: (params?: { year?: number; month?: number; researcher?: string }) =>
    api.get('/business-trips', { params }),
  get: (id: number) => api.get(`/business-trips/${id}`),
  create: (data: any) => api.post('/business-trips', data),
  bulkCreate: (data: any[]) => api.post('/business-trips/bulk', data),
  update: (id: number, data: any) => api.put(`/business-trips/${id}`, data),
  delete: (id: number) => api.delete(`/business-trips/${id}`),
  importExcel: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/business-trips/import-excel', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

export const boardAPI = {
  list: (params?: { search?: string; category?: string; page?: number; page_size?: number }) =>
    api.get('/boards', { params }),
  get: (id: number) => api.get(`/boards/${id}`),
  create: (data: { title: string; content?: string; category?: string; is_pinned?: boolean }) =>
    api.post('/boards', data),
  update: (id: number, data: { title?: string; content?: string; category?: string; is_pinned?: boolean }) =>
    api.put(`/boards/${id}`, data),
  delete: (id: number) => api.delete(`/boards/${id}`),
  uploadAttachments: (boardId: number, files: File[], onProgress?: (e: any) => void) => {
    const formData = new FormData();
    files.forEach((f) => formData.append('files', f));
    return api.post(`/boards/${boardId}/attachments`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: onProgress,
    });
  },
  downloadAttachment: (attachmentId: number) =>
    api.get(`/board-attachments/${attachmentId}/download`, { responseType: 'blob' }),
  deleteAttachment: (attachmentId: number) =>
    api.delete(`/board-attachments/${attachmentId}`),
};

export const registryAPI = {
  catalog: () => api.get('/registry/catalog'),
  tags: (imageName: string) => api.get(`/registry/tags/${imageName}`),
  manifest: (imageName: string, tag: string) => api.get(`/registry/manifest/${imageName}/${tag}`),
  deleteManifest: (imageName: string, digest: string) => api.delete(`/registry/manifest/${imageName}/${digest}`),
  health: () => api.get('/registry/health'),
};

export const storageAPI = {
  health: () => api.get('/storage/health'),
  listObjects: (prefix?: string, delimiter?: string) =>
    api.get('/storage/objects', { params: { ...(prefix ? { prefix } : {}), ...(delimiter ? { delimiter } : {}) } }),
  deleteObject: (objectKey: string) => api.delete(`/storage/objects/${objectKey}`),
  uploadObject: (formData: FormData, prefix?: string) =>
    api.post('/storage/objects/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      params: prefix ? { prefix } : {},
    }),
  createFolder: (folderName: string, prefix?: string) =>
    api.post('/storage/folders', { folder_name: folderName, prefix }),
  getDownloadUrl: (objectKey: string) =>
    api.get(`/storage/objects/${objectKey}/download-url`),
};

export const qrAPI = {
  list: (params?: { target_type?: string; target_id?: number; page?: number; page_size?: number }) =>
    api.get('/qr-codes', { params }),
  generate: (data: { target_type: string; target_id: number; redirect_url: string; description?: string }) =>
    api.post('/qr-codes/generate', data),
  updateRedirect: (shortCode: string, data: { redirect_url?: string; is_active?: boolean; description?: string }) =>
    api.put(`/qr-codes/${shortCode}/redirect`, data),
  logs: (shortCode: string, params?: { page?: number; page_size?: number }) =>
    api.get(`/qr-codes/${shortCode}/logs`, { params }),
  stats: () => api.get('/qr-codes/stats/summary'),
  imageUrl: (shortCode: string) => `/api/qr/${shortCode}/image`,
};

export default api;
